import { Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from '../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

// Living reference for tcg-pwa's MD3 primitives layer — every restyled
// primitive in one place. Not part of the app shell; not linked from
// navigation. Reached at /dev/style-guide (see App.tsx). Mirrors frontend/'s
// /dev/style-guide in spirit, sized to this app's much smaller primitive set
// (6 vs. ~25) — doubles as the template for sub-projects 3-5.
const swatches: { name: string; token: string; on: string }[] = [
  { name: 'Primary', token: 'bg-primary', on: 'text-primary-foreground' },
  { name: 'Secondary Container', token: 'bg-secondary', on: 'text-secondary-foreground' },
  { name: 'Tertiary', token: 'bg-tertiary', on: 'text-on-tertiary' },
  { name: 'Surface', token: 'bg-background', on: 'text-foreground' },
  { name: 'Surface Container', token: 'bg-card', on: 'text-card-foreground' },
  { name: 'Surface Container Low', token: 'bg-muted', on: 'text-muted-foreground' },
  { name: 'Error', token: 'bg-destructive', on: 'text-destructive-foreground' },
  { name: 'Outline', token: 'bg-outline', on: 'text-white' },
];

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-2xl font-medium text-foreground">{title}</h2>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </section>
  );
}

export function StyleGuide() {
  return (
    <div className="min-h-full bg-background px-6 py-10">
      <div className="mx-auto max-w-3xl space-y-16">
        <header className="space-y-2">
          <p className="text-sm font-medium text-primary">TCG Price Lookup</p>
          <h1 className="text-[40px] leading-[1.2] font-medium text-foreground">Material You</h1>
          <p className="max-w-xl text-lg text-muted-foreground">
            Living reference for the MD3 reskin&apos;s primitives layer. Not part of the app
            shell; not linked from navigation.
          </p>
        </header>

        <Section title="Color" description="Purple #6750A4 seed, light mode only.">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {swatches.map((s) => (
              <div key={s.name} className={`${s.token} rounded-2xl p-4 shadow-sm`}>
                <p className={`${s.on} text-sm font-medium`}>{s.name}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Typography" description="Roboto 400/500/700, self-hosted (Latin subset).">
          <div className="space-y-3">
            <p className="text-[40px] leading-[1.2] font-medium">Headline 40</p>
            <p className="text-2xl font-medium">Title Large 24</p>
            <p className="text-base">Body Medium 16 (default)</p>
            <p className="text-sm font-medium">Label Medium 14 (buttons)</p>
            <p className="text-xs">Label Small 12 (captions)</p>
          </div>
        </Section>

        <Section title="Buttons" description="Filled / Tonal / Outlined / Text / FAB — all pill-shaped.">
          <div className="flex flex-wrap items-center gap-3">
            <Button>Filled</Button>
            <Button variant="secondary">Tonal</Button>
            <Button variant="outline">Outlined</Button>
            <Button variant="ghost">Text</Button>
            <Button variant="destructive">Destructive</Button>
            <Button size="fab" variant="fab" aria-label="Add">
              <Plus />
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="lg">Large</Button>
            <Button size="default">Default</Button>
            <Button size="sm">Small</Button>
            <Button size="icon" variant="outline" aria-label="Delete">
              <Trash2 />
            </Button>
            <Button disabled>Disabled</Button>
          </div>
        </Section>

        <Section title="Cards">
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Static card</CardTitle>
                <CardDescription>Surface Container, 24px radius, shadow-sm.</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  No border by default — tonal separation against the page surface.
                </p>
              </CardContent>
              <CardFooter className="gap-2">
                <Button size="sm">Save</Button>
                <Button size="sm" variant="outline">Cancel</Button>
              </CardFooter>
            </Card>
            <Card hoverEffect className="cursor-pointer">
              <CardHeader>
                <CardTitle>Hover card</CardTitle>
                <CardDescription>shadow-md + scale on hover — CardGrid tile shape.</CardDescription>
              </CardHeader>
              <CardContent>
                <Badge>NM</Badge>
              </CardContent>
            </Card>
          </div>
        </Section>

        <Section title="Badges" description="Tonal pills — condition/edition/status colors stay in index.css for now.">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
        </Section>

        <Section title="Input" description="MD3 filled text field — icon composed externally, same as SearchBar today.">
          <div className="max-w-sm space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search cards..." className="pl-10" />
            </div>
            <Input placeholder="Disabled" disabled />
          </div>
        </Section>

        <Section title="Sheet" description="MD3 modal bottom sheet — CartDrawer / ComparisonPanel / CardDetail's target.">
          <Sheet>
            <SheetTrigger asChild>
              <Button>Open sheet</Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Cart</SheetTitle>
                <SheetDescription>3 items · $42.50 total</SheetDescription>
              </SheetHeader>
              <SheetFooter>
                <SheetClose asChild>
                  <Button>Checkout</Button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </Section>

        <Section title="Alert Dialog" description="Centered confirm/cancel — ConfirmDialog's target.">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">Delete session</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this session?</AlertDialogTitle>
                <AlertDialogDescription>
                  This can&apos;t be undone. All items in this cart will be lost.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction variant="destructive">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Section>
      </div>
    </div>
  );
}
